import { ethers } from 'ethers'

// BSC Mainnet configuration
// Removed unused BSC_RPC_URL constant
// Removed unused BSCSCAN_API_KEY constant

// Token addresses (BSC mainnet)
const TOKEN_ADDRESSES: Record<string, string> = {
  'USDT': '0x55d398326f99059fF775485246999027B3197955',
  'USDC': '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
  'BUSD': '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
  'WBNB': '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
  'VOICE': import.meta.env.VITE_VOICE_TOKEN_ADDRESS || '0x3c027958a3D4C4eDb008ee319692F7D387e6C5fB'
}

// ERC-20 ABI (minimal)
const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)'
]

// Voice Token ABI (extended)
export const VOICE_TOKEN_ABI = [
  ...ERC20_ABI,
  'function claimAirdrop(string claimCode, uint8 tier, bytes signature) payable',
  'function getUserTransactions(address user) view returns (uint256[])',
  'function getTransaction(uint256 index) view returns (address from, address to, uint256 amount, uint256 timestamp, string txType)',
  'function getTotalTransactions() view returns (uint256)',
  'function getGasFee() view returns (uint256)',
  'function getFeeCollector() view returns (address)',
  'function hasUserClaimed(address user) view returns (bool)',
  'function getTierAllocation(uint8 tier) view returns (uint256)',
  'function getAirdropStats() view returns (uint256 totalSupply_, uint256 airdropSupply_, uint256 totalClaimed_, uint256 remainingSupply_, uint256 claimPercentage_)',
  'event TransactionRecorded(address indexed from, address indexed to, uint256 amount, string txType)',
  'event AirdropClaimed(address indexed user, uint256 amount, uint8 tier, string claimCode)'
]

interface EthereumTransactionParams {
  recipient: string
  amount: number
  token?: string
  gasLimit?: number
  gasPrice?: string
}

interface TransactionResult {
  hash: string
  success: boolean
  error?: string
}

class EthereumService {
  private provider: ethers.BrowserProvider | null
  private signer: ethers.JsonRpcSigner | null

  constructor() {
    this.provider = null
    this.signer = null
  }

  // Connect to MetaMask wallet
  async connectWallet(): Promise<string> {
    try {
      if (typeof window.ethereum === 'undefined') {
        throw new Error('MetaMask not found. Please install MetaMask wallet.')
      }

      this.provider = new ethers.BrowserProvider(window.ethereum)
      await window.ethereum.request({ method: 'eth_requestAccounts' })
      this.signer = await this.provider.getSigner()
      
      const address = await this.signer.getAddress()
      return address
    } catch (error) {
      console.error('Failed to connect to Ethereum wallet:', error)
      throw error
    }
  }

  // Disconnect wallet
  async disconnectWallet(): Promise<void> {
    this.provider = null
    this.signer = null
  }

  // Get ETH balance
  async getETHBalance(address: string): Promise<number> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }

      const balance = await this.provider.getBalance(address)
      return parseFloat(ethers.formatEther(balance))
    } catch (error) {
      console.error('Failed to get ETH balance:', error)
      throw error
    }
  }

  // Get ERC-20 token balance
  async getTokenBalance(address: string, token: string): Promise<number> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }

      const tokenAddress = TOKEN_ADDRESSES[token.toUpperCase()]
      if (!tokenAddress) {
        throw new Error(`Unsupported token: ${token}`)
      }

      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, this.provider)
      const balance = await contract.balanceOf(address)
      const decimals = await contract.decimals()
      
      return parseFloat(ethers.formatUnits(balance, decimals))
    } catch (error) {
      console.error('Failed to get token balance:', error)
      throw error
    }
  }

  // Get balance (ETH or token)
  async getBalance(address: string, token?: string): Promise<number> {
    if (!token || token === 'ETH') {
      return this.getETHBalance(address)
    } else {
      return this.getTokenBalance(address, token)
    }
  }

  // Send ETH transaction
  async sendETH(params: EthereumTransactionParams): Promise<TransactionResult> {
    try {
      if (!this.signer) {
        throw new Error('Signer not available')
      }

      const transaction: any = {
        to: params.recipient,
        value: ethers.parseEther(params.amount.toString()),
        gasLimit: params.gasLimit || 21000
      }

      if (params.gasPrice) {
        transaction.gasPrice = ethers.parseUnits(params.gasPrice, 'gwei')
      }

      const tx = await this.signer.sendTransaction(transaction)
      await tx.wait()

      return {
        hash: tx.hash,
        success: true
      }
    } catch (error) {
      console.error('Failed to send ETH:', error)
      return {
        hash: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // Send ERC-20 token transaction
  async sendToken(params: EthereumTransactionParams): Promise<TransactionResult> {
    try {
      if (!this.signer || !params.token) {
        throw new Error('Signer not available or token not specified')
      }

      const tokenAddress = TOKEN_ADDRESSES[params.token.toUpperCase()]
      if (!tokenAddress) {
        throw new Error(`Unsupported token: ${params.token}`)
      }

      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, this.signer)
      const decimals = await contract.decimals()
      const amount = ethers.parseUnits(params.amount.toString(), decimals)

      const tx = await contract.transfer(params.recipient, amount, {
        gasLimit: params.gasLimit || 100000
      })
      
      await tx.wait()

      return {
        hash: tx.hash,
        success: true
      }
    } catch (error) {
      console.error('Failed to send token:', error)
      return {
        hash: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // Get transaction history
  async getTransactionHistory(address: string, limit: number = 10): Promise<any[]> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }

      // Get latest block number
      const latestBlock = await this.provider.getBlockNumber()

      // This is a simplified version - in production, you'd use a service like Etherscan API
      const transactions = []
      
      // Get recent blocks and filter transactions
      for (let i = 0; i < Math.min(limit, 100); i++) {
        try {
          const blockNumber = latestBlock - i
          const block = await this.provider.getBlock(blockNumber, true)
          
          if (block && block.transactions) {
            const userTxs = block.transactions.filter((tx: any) => 
              tx.from?.toLowerCase() === address.toLowerCase() || 
              tx.to?.toLowerCase() === address.toLowerCase()
            )
            
            transactions.push(...userTxs.slice(0, limit - transactions.length))
            
            if (transactions.length >= limit) break
          }
        } catch (error) {
          console.error('Error fetching block:', error)
        }
      }

      return transactions
    } catch (error) {
      console.error('Failed to get transaction history:', error)
      throw error
    }
  }

  // Estimate gas for transaction
  async estimateGas(params: EthereumTransactionParams): Promise<number> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }

      if (!params.token || params.token === 'ETH') {
        // ETH transfer
        const gasEstimate = await this.provider.estimateGas({
          to: params.recipient,
          value: ethers.parseEther(params.amount.toString())
        })
        return Number(gasEstimate)
      } else {
        // Token transfer
        const tokenAddress = TOKEN_ADDRESSES[params.token.toUpperCase()]
        if (!tokenAddress) {
          throw new Error(`Unsupported token: ${params.token}`)
        }

        const contract = new ethers.Contract(tokenAddress, ERC20_ABI, this.provider)
        const decimals = await contract.decimals()
        const amount = ethers.parseUnits(params.amount.toString(), decimals)
        
        const gasEstimate = await contract.transfer.estimateGas(params.recipient, amount)
        return Number(gasEstimate)
      }
    } catch (error) {
      console.error('Failed to estimate gas:', error)
      return params.token === 'ETH' ? 21000 : 100000 // Default estimates
    }
  }

  // Get current gas price
  async getGasPrice(): Promise<string> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }

      const feeData = await this.provider.getFeeData()
      return ethers.formatUnits(feeData.gasPrice || 0, 'gwei')
    } catch (error) {
      console.error('Failed to get gas price:', error)
      return '20' // Default 20 gwei
    }
  }

  // Validate Ethereum address
  static validateAddress(address: string): boolean {
    try {
      return ethers.isAddress(address)
    } catch {
      return false
    }
  }

  // Get supported tokens
  static getSupportedTokens(): string[] {
    return ['ETH', ...Object.keys(TOKEN_ADDRESSES)]
  }

  // Get token info
  static getTokenInfo(symbol: string): { address?: string; decimals: number } {
    const tokenInfo: Record<string, { decimals: number }> = {
      'ETH': { decimals: 18 },
      'USDT': { decimals: 6 },
      'USDC': { decimals: 6 },
      'DAI': { decimals: 18 },
      'WETH': { decimals: 18 }
    }

    return {
      address: TOKEN_ADDRESSES[symbol.toUpperCase()],
      decimals: tokenInfo[symbol.toUpperCase()]?.decimals || 18
    }
  }

  // Switch to different network
  async switchNetwork(chainId: string): Promise<void> {
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask not found')
      }

      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }]
      })
    } catch (error) {
      console.error('Failed to switch network:', error)
      throw error
    }
  }

  // Get current network
  async getNetwork(): Promise<{ chainId: number; name: string }> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }

      const network = await this.provider.getNetwork()
      return {
        chainId: Number(network.chainId),
        name: network.name
      }
    } catch (error) {
      console.error('Failed to get network:', error)
      throw error
    }
  }

  // Check if wallet is connected
  isConnected(): boolean {
    return this.provider !== null && this.signer !== null
  }

  // Get wallet address
  async getWalletAddress(): Promise<string | null> {
    try {
      if (!this.signer) {
        return null
      }
      return await this.signer.getAddress()
    } catch {
      return null
    }
  }

  // Voice Token specific functions
  async getVoiceTokenContract(): Promise<ethers.Contract | null> {
    try {
      if (!this.provider) {
        throw new Error('Provider not connected')
      }
      
      const voiceTokenAddress = TOKEN_ADDRESSES['VOICE']
      if (!voiceTokenAddress || voiceTokenAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error('Voice Token address not configured')
      }
      
      return new ethers.Contract(voiceTokenAddress, VOICE_TOKEN_ABI, this.signer || this.provider)
    } catch (error) {
      console.error('Failed to get Voice Token contract:', error)
      return null
    }
  }

  // Claim VOICE airdrop with signature from Telegram bot
  async claimVoiceAirdrop(claimCode: string, tier: number, signature: string): Promise<TransactionResult> {
    try {
      if (!this.signer) {
        throw new Error('Signer not available')
      }

      const contract = await this.getVoiceTokenContract()
      if (!contract) {
        throw new Error('Voice Token contract not available')
      }

      // Get gas fee
      const gasFee = await contract.getGasFee()
      
      console.log('Claiming airdrop with:', {
        claimCode,
        tier,
        signature: signature.substring(0, 10) + '...',
        gasFee: ethers.formatEther(gasFee)
      })

      const tx = await contract.claimAirdrop(claimCode, tier, signature, {
        value: gasFee,
        gasLimit: 300000
      })
      
      const receipt = await tx.wait()
      
      console.log('Airdrop claim successful:', {
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
        gasUsed: receipt.gasUsed.toString()
      })

      return {
        hash: tx.hash,
        success: true
      }
    } catch (error) {
      console.error('Failed to claim airdrop:', error)
      return {
        hash: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // Check if user has claimed airdrop
  async hasClaimedAirdrop(userAddress: string): Promise<boolean> {
    try {
      const contract = await this.getVoiceTokenContract()
      if (!contract) {
        return false
      }

      return await contract.hasUserClaimed(userAddress)
    } catch (error) {
      console.error('Failed to check airdrop status:', error)
      return false
    }
  }

  // Get airdrop statistics
  async getVoiceAirdropStats(): Promise<{ totalClaimed: string; remainingSupply: string }> {
    try {
      const contract = await this.getVoiceTokenContract()
      if (!contract) {
        throw new Error('Voice Token contract not available')
      }

      // These would need to be implemented in the smart contract
      // For now, return placeholder values
      return {
        totalClaimed: '0',
        remainingSupply: '800,000,000'
      }
    } catch (error) {
      console.error('Failed to get airdrop stats:', error)
      return {
        totalClaimed: '0',
        remainingSupply: '0'
      }
    }
  }

  // Get current gas fee for airdrop
  async getVoiceGasFee(): Promise<string> {
    try {
      const contract = await this.getVoiceTokenContract()
      if (!contract) {
        throw new Error('Voice Token contract not available')
      }

      const gasFee = await contract.getGasFee()
      return ethers.formatEther(gasFee)
    } catch (error) {
      console.error('Failed to get gas fee:', error)
      return '0.001' // Default 0.001 BNB
    }
  }



  async getVoiceTransactionHistory(address: string): Promise<any[]> {
    try {
      const contract = await this.getVoiceTokenContract()
      if (!contract) {
        return []
      }

      const userTxIndices = await contract.getUserTransactions(address)
      const transactions = []

      for (const index of userTxIndices) {
        try {
          const tx = await contract.getTransaction(index)
          transactions.push({
            from: tx[0],
            to: tx[1],
            amount: ethers.formatEther(tx[2]),
            timestamp: new Date(Number(tx[3]) * 1000),
            type: tx[4],
            hash: `voice-tx-${index}`
          })
        } catch (error) {
          console.error(`Failed to get transaction ${index}:`, error)
        }
      }

      return transactions.reverse() // Most recent first
    } catch (error) {
      console.error('Failed to get Voice transaction history:', error)
      return []
    }
  }

  // Listen to Voice Token events
  async listenToVoiceEvents(callback: (event: any) => void): Promise<void> {
    try {
      const contract = await this.getVoiceTokenContract()
      if (!contract) {
        return
      }

      // Listen to transaction events
      contract.on('TransactionRecorded', (from, to, amount, txType, event) => {
        callback({
          type: 'TransactionRecorded',
          from,
          to,
          amount: ethers.formatEther(amount),
          txType,
          blockNumber: event.blockNumber,
          transactionHash: event.transactionHash
        })
      })

      // Listen to airdrop claim events
      contract.on('AirdropClaimed', (user, amount, tier, claimCode, event) => {
        callback({
          type: 'AirdropClaimed',
          user,
          amount: ethers.formatEther(amount),
          tier,
          claimCode,
          blockNumber: event.blockNumber,
          transactionHash: event.transactionHash
        })
      })
    } catch (error) {
      console.error('Failed to listen to Voice events:', error)
    }
  }
}

// Export singleton instance
export const ethereumService = new EthereumService()
export default ethereumService